/**
 * Fancy Dress Central — site header (layout metrics only).
 * CSS: assets/fdc-header.css | Markup: sections/header.liquid
 *
 * Fixed header: always visible at top, no scroll animation or hide behaviour.
 */
(function () {
  const BUILD = 'fixed-static-v1';

  function getHeader() {
    return document.querySelector('[data-site-header]');
  }

  function getShell(header) {
    return header?.closest('.shopify-section.cc-header') || header?.closest('.shopify-section');
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 769px)').matches;
  }

  function getAnnouncementHeight() {
    const el =
      document.getElementById('shopify-section-announcement') ||
      document.querySelector('.shopify-section[id*="announcement"]');
    if (!el || el.offsetParent === null) return 0;
    return el.offsetHeight;
  }

  function readHeightVar(name, fallback) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /**
   * Single source for --header-height, --fdc-header-spacer, --header-end.
   */
  function syncHeaderMetrics(header) {
    if (!header) return;
    const shell = getShell(header);
    const desktop = isDesktop();
    const shellHeight = shell?.offsetHeight || readHeightVar('--fdc-header-height-full', 0);
    const announcementHeight = getAnnouncementHeight();
    const totalHeight = shellHeight + announcementHeight;

    let headerEnd = announcementHeight;
    if (shell) {
      const rect = shell.getBoundingClientRect();
      headerEnd = rect.top + shell.offsetHeight;
    }

    document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
    document.documentElement.style.setProperty('--fdc-header-spacer', `${shellHeight}px`);
    document.documentElement.style.setProperty('--fdc-header-height-full', `${shellHeight}px`);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
      '--header-end-padded',
      `${(headerEnd + (desktop ? 56 : 20)).toFixed(1)}px`
    );
  }

  window.fdcSyncHeaderMetrics = syncHeaderMetrics;

  let measureFrame = 0;

  function measureChrome(header) {
    if (!header) return;

    const shell = getShell(header);
    const nav = header.querySelector('.site-header__nav');
    const search = header.querySelector('.site-header__search-inner');
    const navHeight = nav ? nav.scrollHeight : null;
    const searchHeight = search ? search.scrollHeight : null;

    if (navHeight !== null) {
      header.style.setProperty('--site-header-nav-height', `${navHeight}px`);
    }
    if (searchHeight !== null) {
      header.style.setProperty('--site-header-search-height', `${searchHeight}px`);
    }

    syncHeaderMetrics(header);
  }

  function scheduleMeasureChrome(header) {
    if (!header) return;
    if (measureFrame) cancelAnimationFrame(measureFrame);
    measureFrame = requestAnimationFrame(() => {
      measureFrame = 0;
      measureChrome(header);
    });
  }

  function initSearchMinimised(header, transitionMs) {
    const searchToggle = header.querySelector('.js-show-search');
    const searchToggleLeft = header.querySelector('.js-show-search-left');
    const searchBar = header.querySelector('.js-search-bar');

    const toggle = (evt) => {
      evt.preventDefault();
      if (!searchBar) return;

      if (header.classList.contains('search-is-collapsed')) {
        header.classList.remove('search-is-collapsed');
        window.setTimeout(() => {
          header.classList.add('search-is-visible');
          searchBar.querySelector('.js-search-input')?.focus();
          scheduleMeasureChrome(header);
        }, transitionMs);
      } else {
        header.classList.remove('search-is-visible');
        window.setTimeout(() => header.classList.add('search-is-collapsed'), 0);
        window.setTimeout(() => scheduleMeasureChrome(header), transitionMs);
      }
    };

    searchToggle?.addEventListener('click', toggle);
    searchToggleLeft?.addEventListener('click', toggle);
  }

  function initCartShake(header) {
    const icon = header.querySelector('.header__icon--cart-shake');
    if (!icon || !window.theme?.storageUtil) return;

    let pageCount = window.theme.storageUtil.get('shake-page-count', false, true);
    pageCount = pageCount ? parseInt(pageCount, 10) + 1 : 1;
    const shakeFrequency = parseInt(icon.dataset.shakeFrequency, 10);

    if (pageCount < shakeFrequency) {
      icon.classList.remove('header__icon--cart-shake');
    } else {
      pageCount = 0;
    }
    window.theme.storageUtil.set('shake-page-count', pageCount, true);
  }

  function bindHeader(header) {
    if (!header || header.dataset.fdcBound === 'true') return;
    header.dataset.fdcBound = 'true';

    const shell = getShell(header);
    const transitionMs =
      parseFloat(getComputedStyle(header).getPropertyValue('--header-transition-speed')) || 300;

    measureChrome(header);

    if (header.hasAttribute('data-sticky') && shell && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => scheduleMeasureChrome(header));
      observer.observe(shell);
      const inner = header.querySelector('.site-header__inner');
      if (inner && inner !== shell) {
        observer.observe(inner);
      }
    }

    header.querySelector('.main-menu__toggle')?.addEventListener('click', () => {
      scheduleMeasureChrome(header);
    });

    window.addEventListener('on:debounced-resize', () => scheduleMeasureChrome(header));

    if (header.hasAttribute('data-search-minimised')) {
      initSearchMinimised(header, transitionMs);
    }

    initCartShake(header);
  }

  function init() {
    document.documentElement.dataset.fdcHeaderBuild = BUILD;
    bindHeader(getHeader());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (evt) => {
    const header = evt.target?.querySelector?.('[data-site-header]');
    if (header) {
      bindHeader(header);
    }
  });
})();
